import { isEqual, omit } from 'lodash';

import {
  ArticleItem,
  ExternalCollectionItem,
  IssueSectionContainer,
  QueryItem,
  SectionAttributes
} from 'data/generated/graphql';
import { AllessehContentQueryBody } from 'hooks/useAllessehContentQuery';
import type { Container, EntityComponent, TraverseEntityOptions } from './DataModelContext';
import { EntityType } from './DataModelContext.constants';

function isQueryItem(item: unknown): item is QueryItem {
  return typeof item === 'object' && item !== null && (item as { type?: string }).type === EntityType.Query;
}

export function isArticleItem(item: unknown): item is ArticleItem {
  return typeof item === 'object' && item !== null && (item as { type?: string }).type === EntityType.Article;
}

/**
 * Type guard to check if an EntityComponent is an IssueSectionContainer
 * Checks for: Section type, IssueSection subtype, and required non-null fields
 */
export function isIssueSectionContainer(entity: EntityComponent): entity is IssueSectionContainer {
  if (!('type' in entity) || entity.type !== EntityType.Section) {
    return false;
  }

  if (!('subtype' in entity) || entity.subtype !== 'IssueSection') {
    return false;
  }

  if (!('attributes' in entity)) {
    return false;
  }

  const attrs = entity.attributes as SectionAttributes;
  return attrs.sectionKey != null && attrs.sectionLabel != null;
}

function isCollectionItem(item: unknown): item is ExternalCollectionItem {
  return typeof item === 'object' && item !== null && (item as { type?: string }).type === EntityType.Collection;
}

export interface ContentItemCounts {
  articles: number;
  queryItems: number;
  feedItems: number;
  collectionItems: number;
  linkedItems: number;
  totalCount: number;
}

interface GetTotalContentItemCountsOptions {
  /** When true, child nodes whose group is disabled are not traversed. */
  skipDisabledGroups?: boolean;
}

/**
 * Recursively counts leaf content items (articles, queries/feeds, external collections, linked items)
 * under the given entity or array of entities. Queries with `attributes.isFeed === true` are counted
 * separately as feeds.
 */
export function getTotalContentItemCounts(
  entities: EntityComponent | EntityComponent[],
  { skipDisabledGroups = false }: GetTotalContentItemCountsOptions = {}
): ContentItemCounts {
  const roots = Array.isArray(entities) ? entities : [entities];
  const counts: ContentItemCounts = {
    articles: 0,
    queryItems: 0,
    feedItems: 0,
    collectionItems: 0,
    linkedItems: 0,
    totalCount: 0
  };

  const visit = (entity: EntityComponent) => {
    switch (entity.type) {
      case EntityType.Article:
        counts.articles += 1;
        break;
      case EntityType.LinkedItem:
        counts.linkedItems += 1;
        break;
      case EntityType.Query:
        if ((entity as QueryItem).attributes.isFeed) {
          counts.feedItems += 1;
        } else {
          counts.queryItems += 1;
        }
        break;
      case EntityType.Collection:
        counts.collectionItems += 1;
        break;
      default:
        break;
    }

    if ('collection' in entity && Array.isArray(entity.collection)) {
      entity.collection.forEach((child) => {
        if (skipDisabledGroups && 'isGroupEnabled' in child && !!child.groupId && child.isGroupEnabled === false) {
          return;
        }
        visit(child);
      });
    }
  };

  roots.forEach(visit);
  counts.totalCount =
    counts.articles + counts.queryItems + counts.feedItems + counts.collectionItems + counts.linkedItems;
  return counts;
}

export function removeDedupedNotRulesFromTree(
  root: Container,
  removedId: string,
  traverseEntity: (
    entity: EntityComponent,
    callback: (entity: EntityComponent, hierarchyId: string) => void,
    options?: TraverseEntityOptions
  ) => void
) {
  traverseEntity(root, (entity) => {
    if (!isQueryItem(entity)) return;

    const queryBody = entity.attributes.query as AllessehContentQueryBody | undefined;
    const notRules = queryBody?.query?.not;

    if (notRules && Array.isArray(notRules) && queryBody.query) {
      queryBody.query.not = notRules.filter((rule) => {
        if ('term' in rule && rule.term.key === 'UpstreamOriginId') {
          return !(rule.term.value === removedId && rule.meta?.deduped);
        }
        return true;
      });

      // eslint-disable-next-line no-param-reassign
      entity.attributes.query = queryBody as unknown as Record<string, unknown>;
    }
  });
}

/**
 * Custom comparator that ignores specific volatile fields during deep equality checks.
 * This allows us to determine if the data model has meaningfully changed,
 * while ignoring fields that don't represent user modifications:
 * - Collection.attributes.limit: Dynamic query limit, not user-controlled
 * - Collection.contentItems: Resolved data from backend, not user-controlled
 * - Query.attributes.query.count: Dynamic count, not user-controlled
 *
 * Use with lodash's isEqualWith to perform custom equality comparison.
 */
export function isEqualIgnoringVolatileFields(value: unknown, other: unknown): boolean | undefined {
  // For Collection entities, ignore the 'limit' and 'contentItems' fields
  if (isCollectionItem(value) && isCollectionItem(other)) {
    return isEqual(
      omit(value, ['attributes.limit', 'contentItems']),
      omit(other, ['attributes.limit', 'contentItems'])
    );
  }

  // For Query entities, ignore the 'count' field in the query object
  if (isQueryItem(value) && isQueryItem(other)) {
    return isEqual(omit(value, 'attributes.query.count'), omit(other, 'attributes.query.count'));
  }

  // Return undefined to let lodash handle the comparison normally
  return undefined;
}
